import { Spacing } from '@persona-id/design-tokens';
import { EditBoxIcon, LockIcon, PlusIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { FC, useRef, useState } from 'react';
import styled from 'styled-components';

import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import { MenuItem, MenuList } from 'design-system/components/Menu';
import Popover from 'design-system/components/Popover';
import Stack from 'design-system/components/Stack';
import TabBar, { StyledTab, Tab } from 'design-system/components/TabBar';

interface TableViewConfig {
  id: string;
  name: string;
}

export interface TableViewsProps {
  /**
   * Table view tabs
   */
  views?: TableViewConfig[];
  /**
   * Current table view id
   */
  currentView?: string | null;
  /**
   * Called when a view tab is selected
   */
  onSelectView?: (viewId: string | null) => void;
  /**
   * Called when the add view button is clicked. If not provided, the add view button will not be displayed.
   *
   * @returns Promise<{ id: string }> - The id of the newly created view
   */
  onAddView?: () => Promise<{ id: string }>;
  /**
   * Called when the duplicate view button is clicked. If not provided, the duplicate view button will not be displayed.
   *
   * @returns Promise<{ id: string }> - The id of the newly created view
   */
  onDuplicateView?: (viewId: string) => Promise<{ id: string } | void>;
  /**
   * Called when the delete view button is clicked. If not provided, the delete view button will not be displayed.
   */
  onDeleteView?: (viewId: string) => Promise<void>;
  /**
   * Called when the view name is changed. If not provided, the view name will not be editable.
   */
  onViewNameChange?: (viewId: string, name: string) => void;
  /**
   * Called when the save view button is clicked. If not provided, the save view button will not be displayed.
   */
  onSaveView?: () => Promise<void>;
  /**
   * Whether the filters have been changed and can be saved
   */
  isViewDirty?: boolean;
  /**
   * Whether the user has permission to edit views
   */
  canEditViews?: boolean;
}

const AddViewTab = styled(Tab)`
  color: ${({ theme }) => theme.color.text.primary};
`;

const ViewsBar = styled(TabBar)`
  ${AddViewTab} {
    opacity: 0;
  }

  &:hover,
  &:hover:focus,
  &:focus-within {
    ${AddViewTab} {
      opacity: 1;
    }
  }
`;

const TableViews: FC<TableViewsProps> = ({
  currentView,
  onSelectView,
  views,
  onAddView,
  onDuplicateView,
  onDeleteView,
  onViewNameChange,
  onSaveView,
  isViewDirty,
  canEditViews,
}) => {
  const [openPopover, setOpenPopover] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAddView = async () => {
    if (onAddView) {
      const { id } = await onAddView();

      if (currentView !== id) {
        onSelectView?.(id);
      }

      setOpenPopover(true);
    }
  };

  const handleNameChange = (view: TableViewConfig, newName: string) => {
    if (canEditViews && view.name !== newName) {
      onViewNameChange?.(view.id, newName);
    }
  };

  return (
    <ViewsBar
      actions={
        canEditViews &&
        onSaveView && (
          <Button
            id='table-controls__view-tab-save'
            onClick={onSaveView}
            aria-label='Save current view'
            disabled={!isViewDirty}
            size='sm'
            variant='basic'
            css={{ alignSelf: 'center', justifySelf: 'space-between' }}
          >
            Save view
          </Button>
        )
      }
    >
      <Tab
        id='table-controls__view-tab_default'
        selected={!currentView}
        onClick={() => onSelectView?.(null)}
        css={{
          svg: {
            height: 12,
            width: 12,
            marginTop: -2, // Nudge to align visually
            marginLeft: Spacing[1],
          },
        }}
      >
        Default view <LockIcon />
      </Tab>
      {views?.map((view) => {
        const selected = view.id === currentView;

        const tab = (
          <StyledTab
            key={view.id}
            id={`table-controls__view-tab_${view.id}`}
            onClick={!selected ? () => onSelectView?.(view.id) : undefined}
            selected={selected}
            aria-selected={selected}
            role='tab'
          >
            {view.name}
          </StyledTab>
        );

        if (canEditViews && selected) {
          return (
            <Popover
              key={view.id}
              trigger={tab}
              contentProps={{
                align: 'start',
                padding: Spacing[2],
              }}
              open={openPopover}
              onOpenChange={(open) => {
                if (!open && inputRef.current) {
                  handleNameChange(view, inputRef.current.value);
                }

                setOpenPopover(open);
              }}
            >
              <Stack spacing={Spacing[3]}>
                <Input
                  defaultValue={view.name}
                  onBlur={(e) => handleNameChange(view, e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleNameChange(view, e.currentTarget.value);
                    }
                  }}
                  disabled={!onViewNameChange}
                  ref={inputRef}
                />
                <MenuList padding={Spacing[0]}>
                  {onDuplicateView && (
                    <MenuItem
                      id='table-controls__view__menu-duplicate'
                      prefixIcon={<EditBoxIcon />}
                      onClick={async () => {
                        const newTableView = await onDuplicateView(view.id);
                        if (newTableView) {
                          setOpenPopover(true);
                          const { id } = newTableView;
                          if (currentView !== id) {
                            onSelectView?.(id);
                          }
                        }
                      }}
                    >
                      Duplicate
                    </MenuItem>
                  )}
                  {onDeleteView && (
                    <MenuItem
                      id='table-controls__view__menu-delete'
                      prefixIcon={<TrashIcon />}
                      onClick={async () => {
                        await onDeleteView(view.id);
                        setOpenPopover(false);
                        if (currentView) {
                          onSelectView?.(null);
                        }
                      }}
                    >
                      Delete
                    </MenuItem>
                  )}
                </MenuList>
              </Stack>
            </Popover>
          );
        }

        return tab;
      })}
      {canEditViews && onAddView && (
        <AddViewTab
          id='table-controls__view-tab-new'
          onClick={handleAddView}
          aria-label='Add new view'
        >
          <PlusIcon />
        </AddViewTab>
      )}
    </ViewsBar>
  );
};

export default TableViews;
